import { Input } from "@/components/ui/input";
import { Team } from "../../types";
import { GuardFormData } from "./types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { useLanguage } from "@/contexts/LanguageContext";

interface FormFieldProps {
  formData: GuardFormData;
  handleInputChange: (field: keyof GuardFormData) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTeamChange?: (values: string[]) => void;
  teams?: Team[];
}

// Simple label component with forced visibility
const FieldLabel = ({ children, required = false, htmlFor }: { children: React.ReactNode, required?: boolean, htmlFor?: string }) => (
  <label 
    className="block text-sm font-medium text-foreground mb-1" 
    htmlFor={htmlFor}
  >
    {children}
    {required && <span className="text-destructive ml-1">*</span>}
  </label>
);

export const PersonalInfoFields = ({ formData, handleInputChange }: FormFieldProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-2">
        <FieldLabel htmlFor="firstName" required>First Name</FieldLabel>
        <Input
          id="firstName"
          placeholder="Enter first name"
          value={formData.firstName}
          onChange={handleInputChange("firstName")}
          required
        />
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="lastName" required>Last Name</FieldLabel>
        <Input
          id="lastName"
          placeholder="Enter last name"
          value={formData.lastName}
          onChange={handleInputChange("lastName")}
          required
        />
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="gender">Gender</FieldLabel>
        <Select
          value={formData.gender}
          onValueChange={(value) => {
            const event = {
              target: { value }
            } as React.ChangeEvent<HTMLInputElement>;
            handleInputChange("gender")(event);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const ContactFields = ({ formData, handleInputChange }: FormFieldProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <FieldLabel htmlFor="code">Code</FieldLabel>
        <Input
          id="code"
          placeholder="Enter code"
          value={formData.code}
          onChange={handleInputChange("code")}
        />
        <p className="text-xs text-muted-foreground mt-1">Will be auto-generated from VAT if left empty</p>
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="email" required>Email</FieldLabel>
        <Input
          id="email"
          type="email"
          placeholder="Enter email address"
          value={formData.email}
          onChange={handleInputChange("email")}
          required
        />
      </div>
    </div>
  );
};

export const IdentificationFields = ({ formData, handleInputChange }: FormFieldProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <FieldLabel htmlFor="afm" required>VAT</FieldLabel>
        <Input
          id="afm"
          placeholder="Enter VAT"
          value={formData.afm}
          onChange={handleInputChange("afm")}
          required
        />
        <p className="text-xs text-muted-foreground mt-1">Used to generate account credentials</p>
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="mobilePhone">Mobile Phone</FieldLabel>
        <Input
          id="mobilePhone"
          type="tel"
          placeholder="Enter mobile phone"
          value={formData.mobilePhone}
          onChange={handleInputChange("mobilePhone")}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="emergencyContact">Emergency Contact</FieldLabel>
        <Input
          id="emergencyContact"
          type="tel"
          placeholder="Emergency contact phone"
          value={formData.emergencyContact}
          onChange={handleInputChange("emergencyContact")}
        />
      </div>
    </div>
  );
};

export const DateFields = ({ formData, handleInputChange }: FormFieldProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <FieldLabel htmlFor="hiringDate">Hiring Date</FieldLabel>
        <Input
          id="hiringDate"
          type="date"
          value={formData.hiringDate}
          onChange={handleInputChange("hiringDate")}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="dateOfBirth">Date of Birth</FieldLabel>
        <Input
          id="dateOfBirth"
          type="date"
          value={formData.dateOfBirth}
          onChange={handleInputChange("dateOfBirth")}
        />
      </div>
    </div>
  );
};

export const AdditionalFields = ({ formData, handleInputChange, handleTeamChange, teams }: FormFieldProps) => {
  const teamOptions = teams?.map(team => ({
    label: team.name,
    value: team.id
  })) || [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <FieldLabel htmlFor="licenseExpiryDate">License Expiry Date</FieldLabel>
        <Input
          id="licenseExpiryDate"
          type="date"
          value={formData.licenseExpiryDate}
          onChange={handleInputChange("licenseExpiryDate")}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel htmlFor="teams">Teams (Optional)</FieldLabel>
        <MultiSelect
          options={teamOptions}
          selected={formData.selectedTeamIds}
          onChange={(values) => handleTeamChange?.(values)}
          placeholder="Select teams..."
        />
      </div>
    </div>
  );
};